import { useState, useEffect } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface PurchaseForReview {
  id: string;
  product_id: string;
  product_title: string;
  product_type: string;
  provider_id: string;
  provider_name: string;
}

interface ReviewModal {
  purchase: PurchaseForReview;
  onClose: () => void;
  onSubmitted: () => void;
}

function ReviewModal({ purchase, onClose, onSubmitted }: ReviewModal) {
  const { currentUser } = useApp();
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!currentUser?.id) return;
    if (content.trim().length < 10) {
      setError("レビュー内容は10文字以上入力してください。");
      return;
    }
    setSubmitting(true);
    const { error: err } = await supabase.from("reviews").insert({
      reviewer_id: currentUser.id,
      provider_id: purchase.provider_id,
      product_id: purchase.product_id,
      purchase_id: purchase.id,
      rating,
      content: content.trim(),
      product_title: purchase.product_title,
      reviewer_role: "consumer",
      is_hidden: false,
    });
    setSubmitting(false);
    if (err) {
      setError("レビューの送信に失敗しました。既に投稿済みの可能性があります。");
    } else {
      onSubmitted();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-1">Review</p>
            <h3 className="font-serif text-deep-green text-xl">レビューを書く</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/40 cursor-pointer hover:text-charcoal">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <p className="text-sm font-sans text-charcoal/60 mb-5 bg-stone-50 rounded-xl p-3">{purchase.product_title}</p>
        <div className="mb-5">
          <label className="block text-xs font-sans text-charcoal/60 mb-2">評価</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => setRating(n)}
                className={`text-3xl cursor-pointer transition-all ${n <= rating ? "text-gold" : "text-stone-200"} hover:scale-110`}>
                ★
              </button>
            ))}
            <span className="ml-2 self-center text-sm font-sans text-charcoal/60">{rating}.0</span>
          </div>
        </div>
        <div className="mb-5">
          <label className="block text-xs font-sans text-charcoal/60 mb-2">レビュー内容 <span className="text-red-400">*</span></label>
          <textarea value={content} onChange={e => setContent(e.target.value)} maxLength={500} rows={4}
            placeholder="この講座・教材の良かった点、改善点など率直なご意見をお聞かせください（10文字以上）"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
          <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{content.length}/500</p>
        </div>
        {error && (
          <div className="mb-4 text-xs text-red-500 font-sans bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            <i className="ri-error-warning-line mr-1" />{error}
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer hover:border-stone-300">
            キャンセル
          </button>
          <button onClick={handleSubmit} disabled={submitting || content.trim().length < 10}
            className="flex-1 py-3 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap">
            {submitting ? "送信中..." : "レビューを送信 →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReviewableHistory() {
  const { currentUser } = useApp();
  const [purchases, setPurchases] = useState<PurchaseForReview[]>([]);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseForReview | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  const load = async () => {
    if (!currentUser?.id) return;
    const { data: purch } = await supabase.from("purchases").select("id,product_id,product_title,product_type,provider_id,status").eq("buyer_id", currentUser.id).eq("status", "completed").order("purchased_at", { ascending: false });
    if (!purch) { setLoading(false); return; }
    const providerIds = [...new Set(purch.map(p => p.provider_id))];
    const { data: profiles } = await supabase.from("profiles").select("id,name,service_name").in("id", providerIds);
    const pmap = Object.fromEntries((profiles || []).map(p => [p.id, p.service_name || p.name]));
    const purchasesWithNames = purch.map(p => ({ ...p, provider_name: pmap[p.provider_id] || "事業者" }));
    setPurchases(purchasesWithNames);
    const { data: reviews } = await supabase.from("reviews").select("purchase_id").eq("reviewer_id", currentUser.id);
    setReviewedIds(new Set((reviews || []).map(r => r.purchase_id).filter(Boolean)));
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentUser?.id]);

  if (loading) return <div className="flex justify-center py-8"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>;

  const unreviewedPurchases = purchases.filter(p => !reviewedIds.has(p.id));

  if (unreviewedPurchases.length === 0 && purchases.length === 0) return null;

  return (
    <>
      {unreviewedPurchases.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
              <i className="ri-star-line text-gold text-xl" />
            </div>
            <div>
              <p className="font-sans font-medium text-charcoal text-sm">レビューをまだ書いていない購入があります</p>
              <p className="text-xs text-charcoal/50 font-sans mt-0.5">レビューは事業者の励みになります。ぜひご意見をお聞かせください。</p>
            </div>
          </div>
          <div className="space-y-2">
            {unreviewedPurchases.slice(0, 5).map(p => (
              <div key={p.id} className="bg-white rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-sans text-charcoal truncate">{p.product_title}</p>
                  <p className="text-xs text-charcoal/40 font-sans">{p.provider_name}</p>
                </div>
                <button onClick={() => setSelectedPurchase(p)}
                  className="text-xs bg-deep-green text-white px-3 py-1.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap flex-shrink-0">
                  <i className="ri-star-line mr-1" />レビューする
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedPurchase && (
        <ReviewModal
          purchase={selectedPurchase}
          onClose={() => setSelectedPurchase(null)}
          onSubmitted={() => {
            setSuccessId(selectedPurchase.id);
            setReviewedIds(prev => new Set([...prev, selectedPurchase.id]));
            setSelectedPurchase(null);
            setTimeout(() => setSuccessId(null), 3000);
          }}
        />
      )}
      {successId && (
        <div className="fixed bottom-8 right-8 z-50 bg-deep-green text-white px-5 py-3 rounded-2xl font-sans text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-gold" />レビューを送信しました！
        </div>
      )}
    </>
  );
}
